'use client';

import { ReactNode, useEffect, useRef } from 'react';
import Alert from '../alert';
import BevSafeText from '../bev-safe-text';
import { IconAlert } from '../icon';
import { SummarySection, type SummarySectionProps } from './summary-section';

export type StepperFormReviewProps = {
	title?: ReactNode;
	sections: SummarySectionProps[];
	errorMessage?: string;
	children?: ReactNode;
};

function hasContent(node: ReactNode | undefined): boolean {
	if (node === undefined || node === null || node === false) return false;
	if (typeof node === 'string') return node.trim().length > 0;
	return true;
}

export const StepperFormReview = ({
	title,
	sections,
	errorMessage,
	children,
}: StepperFormReviewProps) => {
	const errorRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!errorMessage) return;

		const errorElement = errorRef.current;
		if (!errorElement) return;

		errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
		errorElement.focus({ preventScroll: true });
	}, [errorMessage]);

	return (
		<div className="flex w-full flex-col spacing-stack-l">
			{hasContent(title) ? (
				typeof title === 'string' || typeof title === 'number' ? (
					<h2 className="text-h4 text-text-heading spacing-heading-m">
						<BevSafeText>{title}</BevSafeText>
					</h2>
				) : (
					<div className="spacing-heading-m">
						<BevSafeText>{title}</BevSafeText>
					</div>
				)
			) : null}

			<div className="flex w-full flex-col spacing-stack-l">
				{sections.map((section, sectionIndex) => (
					<SummarySection
						key={sectionIndex}
						title={section.title}
						onEditClick={section.onEditClick}
						columns={section.columns}
						subsections={section.subsections}
					/>
				))}
			</div>

			{errorMessage ? (
				<div ref={errorRef} tabIndex={-1} role="alert">
					<Alert intent="error" kind="subtle" icon={<IconAlert fill="currentColor" />}>
						{errorMessage}
					</Alert>
				</div>
			) : null}

			{children}
		</div>
	);
};
